<template>
  <div class="certificate-generation">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-medal"></i>
              授权证书生成
            </h1>
            <p class="page-description">根据模板生成个性化的授权证书文档</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容 -->
    <div class="main-content">
      <div class="content-container">
        
        <!-- 证书生成表单 -->
        <el-card class="form-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="header-left">
              <i class="el-icon-document-add"></i>
              <span>证书信息填写</span>
            </div>
            <div class="header-actions">
              <el-button size="mini" type="text" @click="showTemplateHelp = true">
                <i class="el-icon-question"></i>
                模板配置说明
              </el-button>
            </div>
          </div>
          
          <el-form 
            ref="certificateForm" 
            :model="form" 
            :rules="rules" 
            label-width="120px"
            class="certificate-form"
          >
            <el-row :gutter="20">
              <!-- 关联门店 -->
              <el-col :span="24">
                <el-form-item label="关联门店">
                  <el-select 
                    v-model="form.storeId" 
                    placeholder="选择要为其生成证书的门店（可选）" 
                    style="width: 100%"
                    clearable
                    filterable
                    @change="handleStoreChange"
                  >
                    <el-option
                      v-for="store in storeList"
                      :key="store.id"
                      :label="`${store.store_name} (${store.contact_person})`"
                      :value="store.id"
                    />
                  </el-select>
                  <div class="form-tip">
                    选择门店后将自动关联证书信息，门店主可在门店详情中查看和下载证书
                  </div>
                </el-form-item>
              </el-col>
              
              <!-- 基本信息 -->
              <el-col :span="12">
                <el-form-item label="被授权人姓名" prop="authorizedName">
                  <el-input 
                    v-model="form.authorizedName" 
                    placeholder="请输入被授权人姓名"
                    prefix-icon="el-icon-user"
                  />
                </el-form-item>
              </el-col>
              
              <el-col :span="12">
                <el-form-item label="证书编号">
                  <el-input 
                    value="系统自动生成 (ZDT + 6位序列号)" 
                    disabled
                    prefix-icon="el-icon-postcard"
                  />
                  <div class="form-tip">证书编号由系统自动生成，格式为 ZDT000001</div>
                </el-form-item>
              </el-col>
              
              <el-col :span="12">
                <el-form-item label="授权身份" prop="role">
                  <el-select v-model="form.role" placeholder="请选择授权身份" style="width: 100%">
                    <el-option label="联合创始人" value="联合创始人"/>
                    <el-option label="合伙人" value="合伙人"/>
                    <el-option label="总代理" value="总代理"/>
                    <el-option label="经销商" value="经销商"/>
                  </el-select>
                </el-form-item>
              </el-col>
              
              <el-col :span="12">
                <el-form-item label="微信号" prop="wechatId">
                  <el-input 
                    v-model="form.wechatId" 
                    placeholder="请输入微信号"
                    prefix-icon="el-icon-message"
                  />
                </el-form-item>
              </el-col>
              
              <!-- 联系信息 -->
              <el-col :span="12">
                <el-form-item label="联系电话">
                  <el-input 
                    v-model="form.authorizedPhone" 
                    placeholder="请输入联系电话（可选）"
                    prefix-icon="el-icon-phone"
                  />
                </el-form-item>
              </el-col>
              
              <el-col :span="12">
                <el-form-item label="授权区域">
                  <el-input 
                    v-model="form.authorizedRegion" 
                    placeholder="请输入授权区域（可选）"
                    prefix-icon="el-icon-location"
                  />
                </el-form-item>
              </el-col>
              
              <el-col :span="24">
                <el-form-item label="证件号码">
                  <el-input 
                    v-model="form.authorizedIdNumber" 
                    placeholder="请输入证件号码（可选）"
                    prefix-icon="el-icon-postcard"
                  />
                </el-form-item>
              </el-col>
              
              <!-- 授权时间 -->
              <el-col :span="12">
                <el-form-item label="授权开始日期" prop="startDate">
                  <el-date-picker
                    v-model="form.startDate"
                    type="date"
                    placeholder="选择开始日期"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
              
              <el-col :span="12">
                <el-form-item label="授权结束日期" prop="endDate">
                  <el-date-picker
                    v-model="form.endDate"
                    type="date"
                    placeholder="选择结束日期"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
              
              <el-col :span="24">
                <el-form-item label="授权单位" prop="issuer">
                  <el-input 
                    v-model="form.issuer" 
                    placeholder="请输入授权单位"
                    prefix-icon="el-icon-office-building"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            
            
            <!-- 操作按钮 -->
            <div class="form-actions">
              <el-button size="large" @click="resetForm">
                <i class="el-icon-refresh-left"></i>
                重置表单
              </el-button>
              <el-button 
                type="primary" 
                size="large" 
                :loading="generating" 
                @click="generateCertificate"
              >
                <i class="el-icon-document-add"></i>
                生成证书
              </el-button>
            </div>
          </el-form>
        </el-card>
        
      </div>
    </div>
    
    <!-- 模板配置说明弹窗 -->
    <certificate-template-help :visible.sync="showTemplateHelp" />
  </div>
</template>

<script>
import { generateAuthorizationCertificate, generateStoreAuthorizationCertificate } from '@/api/system'
import { getStoreList } from '@/api/store'
import CertificateTemplateHelp from './CertificateTemplateHelp.vue'

export default {
  name: 'CertificateGeneration',
  
  components: {
    CertificateTemplateHelp
  },
  
  data() {
    return {
      generating: false,
      showTemplateHelp: false,
      storeList: [],
      form: {
        storeId: null,
        authorizedName: '',
        role: '',
        wechatId: '',
        authorizedPhone: '',
        authorizedRegion: '',
        authorizedIdNumber: '',
        startDate: '',
        endDate: '',
        issuer: '西安祯丹堂健康管理有限公司',
      },
      rules: {
        authorizedName: [
          { required: true, message: '请输入被授权人姓名', trigger: 'blur' }
        ],
        role: [
          { required: true, message: '请选择授权身份', trigger: 'change' }
        ],
        wechatId: [
          { required: true, message: '请输入微信号', trigger: 'blur' }
        ],
        startDate: [
          { required: true, message: '请选择授权开始日期', trigger: 'change' }
        ],
        endDate: [
          { required: true, message: '请选择授权结束日期', trigger: 'change' }
        ],
        issuer: [
          { required: true, message: '请输入授权单位', trigger: 'blur' }
        ]
      }
    }
  },
  
  mounted() {
    this.loadStoreList()
    this.handleUrlParams()
    this.autoFillDates()
  },
  
  methods: {
    // 自动填写授权日期
    autoFillDates() {
      // 只在日期字段为空时自动填写，不覆盖用户已填写的内容
      if (!this.form.startDate && !this.form.endDate) {
        const today = new Date()
        const startDate = new Date(today)
        const endDate = new Date(today)
        
        // 授权开始时间：今天
        this.form.startDate = this.formatDate(startDate)
        
        // 授权结束时间：两年后的前一天
        endDate.setFullYear(endDate.getFullYear() + 2)
        endDate.setDate(endDate.getDate() - 1)
        this.form.endDate = this.formatDate(endDate)
      }
    },
    
    // 格式化日期为YYYY-MM-DD格式
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 处理URL参数
    handleUrlParams() {
      const query = this.$route.query
      if (query.storeId) {
        // 从URL参数预填充门店信息
        this.form.storeId = parseInt(query.storeId)
        this.form.authorizedName = query.contactPerson || ''
        this.form.authorizedPhone = query.contactPhone || ''
        this.form.authorizedRegion = query.address || ''
        
        // 显示提示信息
        this.$nextTick(() => {
          if (query.regenerate === 'true') {
            this.$message({
              type: 'warning',
              message: `正在为门店"${query.storeName}"重新生成证书，新证书将替换原有证书`,
              duration: 6000
            })
          } else {
            this.$message.info(`已为门店"${query.storeName}"预填充证书信息`)
          }
        })
      }
    },
    
    // 加载门店列表
    async loadStoreList() {
      try {
        const res = await getStoreList({ 
          status: 1,  // 只获取已审核通过的门店 (1=已通过)
          page_size: 100 
        })
        this.storeList = res.data?.stores || res.data?.list || []
      } catch (error) {
        console.error('获取门店列表失败:', error)
      }
    },
    
    // 门店选择变化处理
    handleStoreChange(storeId) {
      if (storeId) {
        const selectedStore = this.storeList.find(store => store.id === storeId)
        if (selectedStore) {
          // 自动填充门店相关信息
          this.form.authorizedName = selectedStore.contact_person || ''
          this.form.authorizedPhone = selectedStore.contact_phone || ''
          this.form.authorizedRegion = selectedStore.address || ''
        }
      }
    },
    
    // 生成证书
    async generateCertificate() {
      try {
        await this.$refs.certificateForm.validate()
        
        // 日期验证
        if (new Date(this.form.endDate) <= new Date(this.form.startDate)) {
          this.$message.error('授权结束日期必须晚于开始日期')
          return
        }
        
        this.generating = true
        
        let res
        // 根据是否选择门店决定使用哪个接口
        if (this.form.storeId) {
          console.log('使用门店专用接口生成证书:', this.form.storeId)
          res = await generateStoreAuthorizationCertificate(this.form.storeId, this.form)
          this.$message({
            type: 'success',
            message: '门店授权证书生成成功并已自动绑定！请前往门店详情页面查看证书信息。',
            duration: 5000
          })
        } else {
          console.log('使用通用接口生成证书')
          res = await generateAuthorizationCertificate(this.form)
          this.$message.success('授权证书生成成功！')
        }
        
        // 提供下载
        this.downloadFile(res.data.downloadUrl, res.data.fileName)
        
      } catch (error) {
        console.error('生成证书失败:', error)
        this.$message.error(error.message || '生成证书失败')
      } finally {
        this.generating = false
      }
    },
    
    // 重置表单
    resetForm() {
      this.$refs.certificateForm.resetFields()
    },
    
    // 下载文件
    downloadFile(downloadUrl, fileName) {
      try {
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('开始下载证书文件')
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
/* 主容器 */
.certificate-generation {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

/* 强制覆盖任何缓存样式 */
.certificate-generation .page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3) !important;
}

/* 强制覆盖按钮样式 */
.certificate-generation .form-actions .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
}

/* 强制覆盖图标颜色 */
.certificate-generation .card-header i {
  color: #667eea !important;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==================== 页面头部 ==================== */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.page-description {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  color: white;
}

/* ==================== 主要内容 ==================== */
.main-content {
  padding: 0 40px 40px;
}

.content-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* ==================== 卡片样式 ==================== */
.form-card {
  margin-bottom: 30px;
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions .el-button--text {
  color: #409eff;
  font-size: 13px;
}

.card-header i {
  font-size: 18px;
  color: #667eea;
}

/* ==================== 表单样式 ==================== */
.certificate-form {
  padding: 20px 0;
}

.certificate-form .el-form-item {
  margin-bottom: 25px;
}

.certificate-form .el-input__inner,
.certificate-form .el-select,
.certificate-form .el-date-editor {
  height: 44px;
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  transition: all 0.3s;
}

.certificate-form .el-input__inner:focus,
.certificate-form .el-select:hover,
.certificate-form .el-date-editor:hover {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* ==================== 操作按钮 ==================== */
.form-actions {
  text-align: center;
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #e0e6ed;
}

.form-actions .el-button {
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 15px;
  min-width: 130px;
  margin: 0 10px;
}

.form-actions .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.form-actions .el-button--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

/* ==================== 表单提示样式 ==================== */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}



/* ==================== 响应式设计 ==================== */
@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .main-content {
    padding: 0 20px 20px;
  }
  
  .certificate-form .el-col {
    margin-bottom: 15px;
  }
  
  .form-actions .el-button {
    margin: 5px;
    width: 120px;
  }
}
</style>
